import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bell } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { NotificationType, NotificationPriority } from '@/lib/notifications';
import type { Notification } from '@/lib/notifications';

interface NotificationBellProps {
  notifications: Notification[];
  onNotificationClick: (notification: Notification) => void;
  onClearAll: () => void;
}

export function NotificationBell({
  notifications,
  onNotificationClick,
  onClearAll,
}: NotificationBellProps) {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (notifications) {
      const count = notifications.filter((n) => !n.read).length;
      setUnreadCount(count);
    }
  }, [notifications]);

  // Helper function to get the notification icon based on its type
  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case NotificationType.SYSTEM_ALERT:
      case NotificationType.MESSAGE:
        return 'text-primary';
      case NotificationType.MAINTENANCE_ALERT:
      case NotificationType.TASK_STATUS_CHANGED:
        return 'text-warning';
      case NotificationType.TASK_ASSIGNED:
      case NotificationType.TASK_COMPLETED:
        return 'text-success';
      case NotificationType.CONTRACT_UPDATED:
      case NotificationType.CUSTOM_TERMS_APPROVED:
        return 'text-info';
      default:
        return 'text-muted';
    }
  };

  // Helper function to get the notification priority class
  const getPriorityClass = (priority: NotificationPriority) => {
    switch (priority) {
      case NotificationPriority.HIGH:
        return 'border-l-4 border-destructive';
      case NotificationPriority.MEDIUM:
        return 'border-l-4 border-warning';
      case NotificationPriority.LOW:
      default:
        return 'border-l-4 border-muted';
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-xs text-destructive-foreground">
              {unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between p-4">
          <h3 className="font-medium">Notifications</h3>
          {notifications.length > 0 && (
            <Button variant="ghost" size="sm" onClick={onClearAll}>
              Clear All
            </Button>
          )}
        </div>
        <ScrollArea className="h-[400px]">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No notifications
            </div>
          ) : (
            notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={`flex flex-col items-start p-4 focus:bg-muted/50 ${getPriorityClass(
                  notification.priority
                )}`}
                onClick={() => onNotificationClick(notification)}
              >
                <div className="flex items-center gap-2">
                  <Bell
                    className={`h-4 w-4 ${getNotificationIcon(
                      notification.type
                    )}`}
                  />
                  <span className="font-medium">{notification.title}</span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {notification.message}
                </p>
                <span className="mt-2 text-xs text-muted-foreground">
                  {new Date(notification.timestamp).toLocaleString()}
                </span>
                {!notification.read && (
                  <div className="mt-2 h-2 w-2 rounded-full bg-primary" />
                )}
              </DropdownMenuItem>
            ))
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}